/**
 * Signed bucket links for an acquisition's files, one per (object, path), cached until shortly
 * before they expire. The GPKG is asked for once per mount, a photograph once per opening.
 */
import type { ViewerLink } from '@/api/viewer';

export interface LinkSource {
  /** A URL the browser may GET `path` of `object` with, fetching or refreshing it if needed. */
  url(object: string, path: string): Promise<string>;
  /** Drop a cached link the bucket refused, so the next `url` signs a fresh one. */
  forget?(object: string, path: string): void;
}

/** How long before expiry a cached link is refreshed instead of reused. */
export const LINK_REFRESH_MARGIN_MS = 60_000;

interface Cached {
  url: string;
  expiresAt: number;
}

export function createLinkSource(fetchLink: (object: string, path: string) => Promise<ViewerLink>): LinkSource {
  const cache = new Map<string, Cached>();
  const inFlight = new Map<string, Promise<string>>();

  return {
    url(object, path) {
      const key = `${object}\n${path}`;
      const hit = cache.get(key);
      if (hit && Date.now() < hit.expiresAt - LINK_REFRESH_MARGIN_MS) return Promise.resolve(hit.url);
      const pending = inFlight.get(key);
      if (pending) return pending;
      const request = fetchLink(object, path)
        .then((link) => {
          cache.set(key, { url: link.url, expiresAt: Date.parse(link.expires_at) });
          return link.url;
        })
        .finally(() => inFlight.delete(key));
      inFlight.set(key, request);
      return request;
    },
    forget(object, path) {
      cache.delete(`${object}\n${path}`);
    },
  };
}
